/*
 * album/favorites.ts - the favorites sheet (GALLERY_PLAN.md AL6 / D14) on <dialog id="album_favs">: thumbnails of
 * this browser's ♥ photos (IDs the page doesn't know yet are resolved through /Gallery/Photos?ids=, which returns
 * only photos this visitor may see - a favorite that is no longer visible simply isn't shown), a tile opens the
 * viewer over the favorites, ♥ removes one, "Download all" posts the IDs to #album-zip (the antiforgery form) and
 * the server streams a zip of up to zipMax full-size files. Without album access neither the button nor the form
 * is rendered (the server refuses the zip too).
 */

import { AlbumData, Favorites, Photo, bindHearts, cell, fetchByIds, fmt, toast } from "./shared";
import { Viewer } from "./viewer";

export class FavoritesSheet {
  private photos: Photo[] = [];
  /** id -> the photo, or null when the server didn't return it (not visible to this visitor / gone). */
  private readonly resolved = new Map<string, Photo | null>();
  private readonly grid: HTMLElement;
  private readonly zip: HTMLButtonElement | null;
  private renderGen = 0;
  private readonly q = <T extends Element>(sel: string): T => this.dialog.querySelector<T>(sel)!;

  constructor(
    private readonly dialog: HTMLDialogElement,
    private readonly data: AlbumData,
    private readonly favs: Favorites,
    private readonly viewer: Viewer,
    private readonly zipForm: HTMLFormElement | null,
  ) {
    this.grid = this.q<HTMLElement>("[data-fs-grid]");
    this.zip = this.dialog.querySelector<HTMLButtonElement>("[data-fs-zip]");

    this.q("[data-fs-close]").addEventListener("click", () => this.dialog.close());
    // a click on the backdrop (outside the sheet's box) closes it
    dialog.addEventListener("click", (e) => {
      if (e.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      const me = e as MouseEvent;
      if (me.clientX < r.left || me.clientX > r.right || me.clientY < r.top || me.clientY > r.bottom) dialog.close();
    });

    this.grid.addEventListener("click", (e) => {
      const me = e as MouseEvent;
      if (me.button !== 0 || me.ctrlKey || me.metaKey || me.shiftKey || me.altKey) return;
      const a = (e.target as HTMLElement).closest<HTMLAnchorElement>("a[data-photo]");
      if (!a) return;
      const i = this.photos.findIndex((p) => p.id === a.dataset.photo);
      if (i < 0) return;
      e.preventDefault();
      this.viewer.open(this.photos.slice(), i);
    });
    bindHearts(this.grid, favs, data);
    this.zip?.addEventListener("click", () => this.download());

    favs.onChange(() => {
      if (this.dialog.open) void this.render();
    });
  }

  open(): void {
    if (!this.dialog.open) this.dialog.showModal();
    void this.render();
  }

  private async render(): Promise<void> {
    const gen = ++this.renderGen;
    const ids = this.favs.list();

    // the page already knows the photos it rendered or loaded (all visible to this visitor)
    ids.forEach((id) => {
      const p = this.data.photos[id];
      if (p && !this.resolved.get(id)) this.resolved.set(id, p);
    });
    const unknown = ids.filter((id) => !this.resolved.has(id));
    if (unknown.length) {
      this.paint(ids);   // what is known now; the rest follows
      try {
        const found = await fetchByIds(unknown);
        found.forEach((p) => {
          this.data.photos[p.id] = this.data.photos[p.id] ?? p;
          this.resolved.set(p.id, p);
        });
        unknown.forEach((id) => {
          if (!this.resolved.has(id)) this.resolved.set(id, null);
        });
      } catch {
        toast(this.data.i18n.loadFail);
      }
      if (gen !== this.renderGen) return;
    }
    this.paint(ids);
  }

  private paint(ids: string[]): void {
    this.photos = ids.map((id) => this.resolved.get(id)).filter((p): p is Photo => !!p);
    this.grid.replaceChildren(...this.photos.map((p) => cell(p, this.favs, this.data)));
    this.q<HTMLElement>("[data-fs-count]").textContent = String(this.photos.length);
    this.q<HTMLElement>("[data-fs-empty]").hidden = this.photos.length > 0;
    if (!this.zip) return;
    this.zip.disabled = this.photos.length === 0;
    const note = this.q<HTMLElement>("[data-fs-note]");
    const over = this.photos.length > this.data.zipMax;
    note.hidden = !over;
    note.textContent = over ? fmt(this.data.i18n.favsMax, this.data.zipMax) : "";
  }

  /** "Download all": the first zipMax favorites as one zip (POST /Gallery/Zip - a file response, the page stays). */
  private download(): void {
    if (!this.zipForm || !this.photos.length) return;
    this.zipForm.querySelectorAll("input[name='ids']").forEach((i) => i.remove());
    this.photos.slice(0, this.data.zipMax).forEach((p) => {
      const i = document.createElement("input");
      i.type = "hidden";
      i.name = "ids";
      i.value = p.id;
      this.zipForm!.appendChild(i);
    });
    this.zipForm.submit();
    toast(this.data.i18n.favsPreparing);
  }
}
